import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, Thermometer, Wind, Flame, Clock, Route as RouteIcon, MapPin } from "lucide-react";
import { api } from "../../lib/api";
import RouteMap from "./RouteMap";
import { logError } from "../../lib/logError";
import { WEATHER_ICON, sevBadge, sevText } from "../../lib/weather";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export default function Network() {
  const [data, setData] = useState(null);
  const [ago, setAgo] = useState(0);
  const [selId, setSelId] = useState("");

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await api.get("/network");
        if (mounted) { setData(res.data); setAgo(0); }
      } catch (e) { logError("network.load", e); }
    };
    load();
    const poll = setInterval(load, 45000);
    const tick = setInterval(() => setAgo((a) => a + 1), 1000);
    return () => { mounted = false; clearInterval(poll); clearInterval(tick); };
  }, []);

  const routes = data?.routes || [];
  const hotRoutes = routes.filter((r) => r.hot);

  useEffect(() => {
    if (!data?.routes?.length || selId) return;
    const rs = data.routes;
    setSelId((rs.find((r) => r.hot) || rs[0]).id);
  }, [data, selId]);

  const sel = routes.find((r) => r.id === selId);
  const SelIcon = sel ? (WEATHER_ICON[sel.icon] || WEATHER_ICON.cloud) : null;

  return (
    <section id="network" data-testid="network-section" className="relative py-28 md:py-40 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="font-mono-x text-primary text-xs tracking-[0.35em]">/ 05 — LIVE NETWORK</span>
            <h2 className="mt-6 font-heading font-extrabold tracking-tighter text-4xl md:text-5xl">North-American Lane Network</h2>
            <p className="mt-4 max-w-xl text-muted-foreground">Pick any lane to see live, weather-aware conditions and a real-time ETA. Our hottest, highest-demand lanes are flagged below.</p>
          </div>
          <div className="flex items-center gap-2 font-mono-x text-xs text-muted-foreground">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> LIVE · updated {ago}s ago
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="lg:col-span-2 rounded-2xl border border-border overflow-hidden glass p-2">
            <RouteMap routes={routes} animate selectedId={selId} height="560px" />
            <div className="flex flex-wrap gap-3 p-4">
              {routes.map((r) => (
                <button key={r.id} data-testid={`route-legend-${r.id}`} onClick={() => setSelId(r.id)}
                  className={`flex items-center gap-2 text-sm rounded-full px-3 py-1.5 border transition-colors ${selId === r.id ? "border-primary bg-primary/10" : "border-transparent hover:border-border"}`}>
                  <span className="w-3 h-3 rounded-full" style={{ background: r.color }} />
                  <span className="font-medium">{r.name}</span>
                </button>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-2xl border border-border glass p-6">
            <div className="flex items-center gap-2 mb-5">
              <Activity className="w-4 h-4 text-primary" />
              <h3 className="font-heading font-bold text-lg">Check a lane</h3>
            </div>

            {hotRoutes.length > 0 && (
              <div className="mb-5">
                <div className="text-[10px] font-mono-x tracking-[0.25em] text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Flame className="w-3 h-3 text-orange-500" /> HOT LANE SUGGESTIONS
                </div>
                <div className="flex flex-wrap gap-2">
                  {hotRoutes.map((r) => (
                    <button key={r.id} data-testid={`hot-suggestion-${r.id}`} onClick={() => setSelId(r.id)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${selId === r.id ? "bg-primary text-primary-foreground border-primary" : "border-orange-500/40 text-orange-500 hover:bg-orange-500/10"}`}>
                      {r.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Select value={selId} onValueChange={setSelId}>
              <SelectTrigger data-testid="lane-select" className="h-11"><SelectValue placeholder="Select a lane" /></SelectTrigger>
              <SelectContent>
                {routes.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`lane-opt-${r.id}`}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>

            {sel && (
              <div data-testid="lane-detail" className="mt-5 rounded-xl border border-border bg-card/40 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ background: sel.color }} />
                    {sel.name}
                    {sel.hot && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-mono-x tracking-widest px-1.5 py-0.5 rounded-full bg-orange-500/15 text-orange-500">
                        <Flame className="w-2.5 h-2.5" /> HOT
                      </span>
                    )}
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full border whitespace-nowrap ${sevBadge(sel.severity)}`}>{sel.impact}</span>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2">
                  <div data-testid="lane-eta-transit" className="rounded-lg bg-background/50 border border-border p-2.5 text-center">
                    <div className="flex items-center justify-center gap-1 text-primary"><Clock className="w-3.5 h-3.5" /><span className="font-heading font-extrabold text-lg">{sel.base_hours}h</span></div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">base transit</div>
                  </div>
                  <div data-testid="lane-eta-weather" className="rounded-lg bg-background/50 border border-border p-2.5 text-center">
                    <div className={`font-heading font-extrabold text-lg ${sel.total_delay_hours > 0 ? "text-orange-500" : "text-emerald-500"}`}>+{sel.total_delay_hours}h</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">weather delay</div>
                  </div>
                  <div data-testid="lane-eta-arrival" className="rounded-lg bg-primary/10 border border-primary/30 p-2.5 text-center">
                    <div className="font-heading font-extrabold text-sm text-primary leading-tight mt-0.5">{sel.eta}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">est. arrival</div>
                  </div>
                </div>
                <div className="mt-2 text-[11px] text-muted-foreground font-mono-x flex items-center gap-1.5">
                  <RouteIcon className="w-3 h-3" /> {sel.distance_km?.toLocaleString()} km · {sel.eta_hours}h total incl. weather
                </div>

                <div className="mt-4 space-y-2">
                  <div className="text-[10px] font-mono-x tracking-[0.25em] text-muted-foreground mb-1">STOP-BY-STOP WEATHER</div>
                  {sel.stops?.map((s, i) => {
                    const Icon = WEATHER_ICON[s.icon] || WEATHER_ICON.cloud;
                    return (
                      <div key={s.city + i} data-testid={`lane-stop-${i}`} className="flex items-center justify-between gap-2 text-xs">
                        <span className="flex items-center gap-1.5 font-medium"><MapPin className="w-3 h-3 text-muted-foreground" /> {s.city}</span>
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <Icon className={`w-3.5 h-3.5 ${sevText(s.severity)}`} /> {s.condition}
                          <span className="flex items-center gap-0.5"><Thermometer className="w-3 h-3" />{s.temp}°</span>
                          <span className="flex items-center gap-0.5"><Wind className="w-3 h-3" />{s.wind}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
